"use client"

import type { ComponentType, SVGProps } from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"

import { cn } from "@productlane/lib/src/browser"

import { Tooltip } from "./tooltip"

export interface SegmentedControlOption<T extends string> {
	value: T
	label: string
	/** A second tooltip line under the label, for icon-only controls. */
	description?: string
	Icon?: ComponentType<SVGProps<SVGSVGElement>>
}

interface Props<T extends string> {
	name: string
	/** null selects nothing, for a group whose items disagree. */
	value: T | null
	options: readonly SegmentedControlOption<T>[]
	onValueChange: (value: T) => void
	showLabels?: boolean
	className?: string
	"aria-label"?: string
}

export function SegmentedControl<T extends string>({
	name,
	value,
	options,
	onValueChange,
	showLabels = false,
	className,
	"aria-label": ariaLabel,
}: Props<T>) {
	return (
		<RadioGroupPrimitive.Root
			name={name}
			value={value ?? ""}
			onValueChange={(next) => onValueChange(next as T)}
			orientation="horizontal"
			aria-label={ariaLabel}
			className={cn(
				"inline-flex flex-none items-center gap-0.5 rounded-lg bg-gray-100 p-0.5 dark:bg-gray-800",
				className,
			)}
		>
			{options.map((option) => (
				// Wrapped, so the tooltip's own data-state cannot clobber the
				// item's data-state="checked" that drives the selected look.
				<Tooltip
					key={option.value}
					label={
						option.description ? (
							<span className="block max-w-56 py-0.5">
								<span className="block font-medium text-primary">
									{option.label}
								</span>
								<span className="mt-0.5 block">{option.description}</span>
							</span>
						) : (
							option.label
						)
					}
					enabled={!showLabels}
					shouldWrapChildren
				>
					<RadioGroupPrimitive.Item
						value={option.value}
						aria-label={option.label}
						className={cn(
							"flex h-6 items-center justify-center gap-1.5 rounded-[0.4rem] text-xs font-medium text-tertiary outline-none transition-colors duration-150 ease-out",
							"hover:text-secondary focus-visible:ring-1 focus-visible:ring-blue-500",
							"data-[state=checked]:bg-white data-[state=checked]:text-primary data-[state=checked]:shadow-sm data-[state=checked]:ring-1 data-[state=checked]:ring-black/[0.08]",
							"dark:data-[state=checked]:bg-gray-700 dark:data-[state=checked]:ring-white/[0.10]",
							showLabels ? "px-2" : "w-7",
						)}
					>
						{option.Icon && (
							<option.Icon
								className="h-3.5 w-3.5 flex-none transform-gpu"
								strokeWidth={1.5}
							/>
						)}
						{showLabels ? option.label : null}
					</RadioGroupPrimitive.Item>
				</Tooltip>
			))}
		</RadioGroupPrimitive.Root>
	)
}
